import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useEffect, useState } from "react";
import {
  usePlanById,
  useUpdatePaymentAndCreateSubscription,
} from "../../../queries/auth";
import { useUpdateAccount } from "../../../queries/ghlAuth";
import { useUpdateAssistant, useApplyCoupon } from "../../../queries/pricing";
import { useUsersContext } from "../../../context/usersContext";
import { useUserInfo } from "../../../context/user";
import toast from "react-hot-toast";
import { loadStripe } from "@stripe/stripe-js";
import { useNavigate } from "react-router-dom";
import AuthTopBar from "../component/AuthTopBar";
import { useTheme } from "../../../hook/useTheme";
import DoneIcon from "@mui/icons-material/Done";

const AddPayment = () => {
  const logout = useUserInfo()[2];
  const navigate = useNavigate();
  const updateAccountMutation = useUpdateAccount();
  const [, , , saveActiveLocation, userActiveLocation] = useUserInfo();
  const [accountLoading, setAccountLoading] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(null);
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);

  const agencyId = userActiveLocation?.agency_id;
  const { agency, isAgencyLoading } = useUsersContext();
  const { data: planResponse, isLoading: planLoading } = usePlanById(userActiveLocation?.plan_id, {
    enabled: !!agencyId,
  });
  const plandata = planResponse?.data;
  // Mirrors the old manual `loading` state: true until the agency_id shows up
  // and both reads have resolved once (both were gated on agency_id alone).
  const loading = !agencyId || isAgencyLoading || planLoading;

  // Handle Carousel Timer
  useEffect(() => {
    if (!agency?.account_activation_quote) return;

    let quotesCount = 0;
    try {
      const parsed =
        typeof agency.account_activation_quote === "string"
          ? JSON.parse(agency.account_activation_quote)
          : agency.account_activation_quote;
      if (parsed.quote1?.text) quotesCount++;
      if (parsed.quote2?.text) quotesCount++;
      if (parsed.quote3?.text) quotesCount++;
    } catch (e) {
      return;
    }

    if (quotesCount <= 1) return;

    const interval = setInterval(() => {
      setCurrentQuoteIndex((prev) => (prev + 1) % quotesCount);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (plandata?.is_free_saas) {
      if (
        plandata.promptlabel_ids?.length > 0 &&
        userActiveLocation?.assistants?.length === 0
      ) {
        handleAddAssistantsInAccount();
      } else {
        navigate("/app/chat");
      }
    }
  }, [plandata]);

  const handleAddAssistantsInAccount = async () => {
    setAccountLoading(true);
    let response;
    try {
      response = await updateAccountMutation.mutateAsync({
        _id: userActiveLocation?.id,
        promptlabels_ids: plandata?.promptlabel_ids,
      });
    } catch (error) {
      setAccountLoading(false);
      navigate("/app/chat");
      return;
    }
    if (response?.data) {
      saveActiveLocation({
        ...userActiveLocation,
        ...response?.account,
      });
    }
    setAccountLoading(false);
    navigate("/app/chat");
  };

  if (!agency?.stripe?.publish_key || accountLoading) {
    return (
      <div className="h-screen bg-background text-foreground">
        <AuthTopBar />
        <div className="relative flex justify-center">
          {/* Escape hatch: plan-less accounts are force-routed here, so this
              guard screen MUST offer logout or the user is trapped (CU-86cank12f).
              Themed with tokens like the rest of the page. */}
          <p className="text-muted-foreground absolute top-7 right-12 text-sm">
            Already have an account?{" "}
            <button
              className="text-foreground font-semibold hover:underline"
              onClick={() => {
                logout();
                navigate("/auth/sign-in");
              }}
            >
              Sign in
            </button>
          </p>
          {loading || accountLoading ? (
            <div className="spinner !w-30 !h-30 mt-28"></div>
          ) : (
            <div className="mt-28 max-w-md w-full mx-6 rounded-xl border border-border bg-card text-card-foreground shadow-sm p-8 text-center">
              <h2 className="text-xl font-bold text-foreground">
                Billing isn't set up for this account yet
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
                We can't take payments for this account right now. Please speak
                to your partner manager and ask them to check the billing
                configuration for this account.
              </p>
              <div className="mt-5 inline-flex items-center gap-2 rounded-md border border-border bg-muted px-3 py-2">
                <span className="text-xs uppercase tracking-wider text-muted-foreground">
                  Account ID
                </span>
                <span className="font-mono text-sm text-foreground">
                  {userActiveLocation?._id}
                </span>
              </div>
              <button
                type="button"
                onClick={() => navigate("/app/settings#billing")}
                className="mt-6 block w-full font-bold py-3 rounded-md bg-foreground text-background hover:bg-foreground/90 transition-colors text-[15px]"
              >
                Go to Billing
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
  const stripePromise = loadStripe(agency?.stripe?.publish_key);

  const calculations = () => {
    return userActiveLocation?.planType === "monthly"
      ? !couponDiscount
        ? plandata?.monthly_amount / 100
        : couponDiscount?.coupon?.percent_off
          ? plandata?.monthly_amount / 100 -
          (couponDiscount?.coupon?.percent_off / 100) *
          (plandata?.monthly_amount / 100)
          : plandata?.monthly_amount / 100 -
          parseInt(couponDiscount?.coupon?.amount_off) / 100
      : !couponDiscount
        ? plandata?.yearly_amount / 100
        : couponDiscount?.coupon?.percent_off
          ? plandata?.yearly_amount / 100 -
          (couponDiscount?.coupon?.percent_off / 100) *
          (plandata?.yearly_amount / 100)
          : plandata?.yearly_amount / 100 -
          parseInt(couponDiscount?.coupon?.amount_off) / 100;
  };

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <div className="relative">
        <AuthTopBar />
        {/* Overlaid on the themed AuthTopBar — must use tokens, not fixed dark */}
        <p className="text-muted-foreground absolute top-1/2 -translate-y-1/2 right-12 text-sm hidden md:block">
          Already have an account?{" "}
          <button
            className="text-foreground font-semibold hover:underline"
            onClick={() => {
              logout();
              navigate("/auth/sign-in");
            }}
          >
            Sign In
          </button>
        </p>
      </div>

      <div className="flex-1 flex flex-col md:flex-row">
        {/* Left side - form */}
        <div className="w-full md:w-1/2 flex flex-col items-center py-12 px-6 md:py-20">
          <div className="max-w-[440px] w-full">
            {plandata?.trial_and_credit?.active_trile_period ? (
              <h2 className="text-[34px] font-bold mb-4 leading-tight text-foreground">
                {`Start Your ${plandata?.trial_and_credit?.days} Day Free Trial`}
              </h2>
            ) : (
              <h2 className="text-[34px] font-bold mb-4 leading-tight text-foreground">
                Activate Your Account And <br />
                Build AI Agents Without Limits
              </h2>
            )}
            <div className="text-muted-foreground text-[17px] mb-10 leading-relaxed">
              {plandata?.trial_and_credit?.active_trile_period ? (
                <>
                  <p className="mb-4">
                    Add your card details to turn ideas into AI Agents in
                    minutes. No charge today.
                  </p>
                  <p className="font-medium">Cancel anytime. No risk.</p>
                </>
              ) : (
                <p>
                  Give your team unlimited AI Agents, instant access to every
                  tool, and founder-led coaching that gets results fast.
                </p>
              )}
            </div>

            <Elements stripe={stripePromise}>
              <StripeForm
                agency={agency}
                userActiveLocation={userActiveLocation}
                setCouponDiscount={setCouponDiscount}
                plandata={plandata}
              />
            </Elements>

            {plandata?.trial_and_credit?.days ? (
              <p className="text-xs text-center text-muted-foreground mt-8">
                No payment for {plandata?.trial_and_credit?.days || "x"} days.
                Then{" "}
                {plandata?.category?.name === "GBP"
                  ? "£"
                  : plandata?.category?.name === "USD"
                    ? "$"
                    : "£"}
                {calculations()}{" "}
                {`per ${userActiveLocation?.planType === "monthly" ? "month" : "year"
                  }. Cancel anytime`}
              </p>
            ) : (
              <p className="text-xs text-center text-muted-foreground mt-8">
                {plandata?.category?.name === "GBP"
                  ? "£"
                  : plandata?.category?.name === "USD"
                    ? "$"
                    : "£"}
                {calculations()}{" "}
                {`per ${userActiveLocation?.planType === "monthly" ? "month" : "year"
                  }. Cancel anytime.`}
              </p>
            )}
          </div>
        </div>

        {/* Right side - benefits & testimonials */}
        <div className="w-full md:w-1/2 bg-muted flex flex-col items-center py-12 px-6 md:py-20 gap-8">
          {/* Key Benefits Card */}
          <div className="bg-card text-card-foreground border border-border rounded-md shadow-md p-8 md:p-10 max-w-[480px] w-full">
            <h3 className="font-bold text-lg mb-8 text-foreground">Key Benefits</h3>
            <ul className="space-y-6">
              {[
                "Deploy prebuilt AI Agents",
                "Test real-world AI use cases in your business before you commit",
                "Join daily workshops with our founders",
                "ROI Playbook + Assessor Agent included",
                "24/7 Support",
              ].map((benefit, idx) => (
                <li key={idx} className="flex items-start">
                  <span className="text-[#869afd] mr-4 mt-0.5">
                    <DoneIcon fontSize="small" />
                  </span>
                  <p className="text-muted-foreground text-[15px] leading-snug">
                    {agency?.keyBenefits?.[idx] || benefit}
                  </p>
                </li>
              ))}
            </ul>
          </div>

          {/* Account Activation Quotes */}
          {(() => {
            let quotes = [];
            try {
              if (agency?.account_activation_quote) {
                const parsed =
                  typeof agency.account_activation_quote === "string"
                    ? JSON.parse(agency.account_activation_quote)
                    : agency.account_activation_quote;

                if (parsed.quote1?.text) quotes.push(parsed.quote1);
                if (parsed.quote2?.text) quotes.push(parsed.quote2);
                if (parsed.quote3?.text) quotes.push(parsed.quote3);
              }
            } catch (e) {
              console.error("Error parsing quotes", e);
            }

            if (quotes.length === 0) return null;

            const activeQuote = quotes[currentQuoteIndex] || quotes[0];

            return (
              <div className="max-w-[480px] w-full">
                <div className="bg-card text-card-foreground border border-border rounded-md shadow-md p-8 md:p-10 flex flex-col justify-between min-h-[250px] transition-all duration-500">
                  <h3 className="text-foreground text-lg mb-8 leading-relaxed font-medium">
                    "{activeQuote.text}"
                  </h3>

                  <div className="flex items-center">
                    {activeQuote.image && (
                      <div className="w-12 h-12 mr-4 rounded-full overflow-hidden bg-muted shrink-0">
                        <img
                          src={activeQuote.image}
                          alt={activeQuote.author_name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}

                    <div>
                      {activeQuote.author_name && (
                        <p className="font-bold text-sm text-foreground">
                          {activeQuote.author_name}
                        </p>
                      )}
                      {activeQuote.job_title && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {activeQuote.job_title}
                        </p>
                      )}
                    </div>
                  </div>
                  {/* Carousel Dots */}
                  {quotes.length > 1 && (
                    <div className="flex justify-center gap-2 mt-8">
                      {quotes.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentQuoteIndex(idx)}
                          className={`w-2 h-2 rounded-full transition-all duration-300 ${currentQuoteIndex === idx
                            ? "bg-[#869afd]"
                            : "bg-border hover:bg-muted-foreground/40"
                            }`}
                          aria-label={`Go to quote ${idx + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default AddPayment;

const StripeForm = ({
  agency,
  userActiveLocation,
  setCouponDiscount,
  plandata,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [couponLoading, setCouponLoading] = useState(false);
  const [showCoupon, setShowCoupon] = useState(false);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [coupon, setCoupon] = useState("");
  const [postcode, setPostcode] = useState("");
  const updateAssistantMutation = useUpdateAssistant();
  const applyCouponMutation = useApplyCoupon();
  const { resolved: resolvedTheme } = useTheme();
  const saveActiveLocation = useUserInfo()[5];
  const userInfo = useUserInfo()[0];
  const navigate = useNavigate();
  const updatePaymentMutation = useUpdatePaymentAndCreateSubscription();

  useEffect(() => {
    if (plandata?.is_free_saas) {
      if (
        plandata.promptlabel_ids?.length > 0 &&
        userActiveLocation?.assistants?.length === 0
      ) {
        handleAddAssistantsInAccount();
      } else {
        navigate("/app/chat");
      }
    }
  }, [plandata]);

  const cardOptions = {
    style: {
      base: {
        fontSize: "16px",
        fontFamily: '"Inter", sans-serif',
        color: resolvedTheme === "dark" ? "#f5f5f5" : "#020817",
        "::placeholder": {
          color: resolvedTheme === "dark" ? "#a3a3a3" : "#64748b",
        },
      },
      invalid: {
        color: resolvedTheme === "dark" ? "#c22222" : "#ef4444",
      },
    },
  };


  const handleAddAssistantsInAccount = async () => {
    let response;
    try {
      response = await updateAssistantMutation.mutateAsync({
        _id: userActiveLocation?.id,
        promptlabels_ids: plandata?.promptlabel_ids,
      });
    } catch (error) {
      navigate("/app/chat");
      return;
    }
    if (response?.data) {
      saveActiveLocation({
        ...userActiveLocation,
        ...response.data,
      });
    }
    navigate("/app/chat");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!stripe || !elements) {
      setLoading(false);
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
      billing_details: {
        name,
        email: userActiveLocation?.email,
        address: {
          postal_code: postcode,
        },
      },
    });

    if (error) {
      setError(error.message);
      setLoading(false);
      return;
    }

    let result;
    try {
      result = await updatePaymentMutation.mutateAsync({
        paymentPayload: {
          id: paymentMethod.id,
          agency_id: agency?._id,
          customer_id: userActiveLocation?.customer_id,
        },
        subscriptionPayload: {
          agency_id: agency?._id,
          price_id:
            userActiveLocation?.planType === "monthly"
              ? plandata?.monthly_id
              : plandata?.yearly_id,
          plan_id: plandata?._id,
          account_id: userActiveLocation?._id,
          paymentMethod_id: paymentMethod.id,
          customer_id: userActiveLocation?.customer_id,
          code: coupon,
          userId: userInfo?._id,
        },
      });
    } catch (err) {
      // Only a genuine payment-method-update failure reaches here — see
      // queries/auth.js's updatePaymentMethodAndCreateSubscription for why
      // create-subscription failures never throw.
      const message = err?.response?.data?.message;
      if (message) {
        toast.error(
          message === "Your card was declined."
            ? "Your card was declined. please try with another"
            : message,
          "error",
        );
      }
      setLoading(false);
      return;
    }

    if (result.paymentUpdated && (result.subscription || result.incomplete)) {
      const subscription = result.subscription;
      const latestInvoice = subscription?.latest_invoice;
      const paymentIntent = latestInvoice?.payment_intent;

      if (
        paymentIntent &&
        (paymentIntent.status === "requires_action" ||
          paymentIntent.status === "requires_confirmation")
      ) {
        const { error: confirmError } = await stripe.confirmCardPayment(
          paymentIntent.client_secret,
        );

        if (confirmError) {
          toast.error(confirmError.message);
          return;
        }
      }

      saveActiveLocation(result.account);
      toast.success("Subscription created successfully!");
      setTimeout(() => {
        navigate("/app/chat");
      }, 300);
    } else if (result.paymentUpdated && result.alreadySubscribed) {
      saveActiveLocation(result.account);
      setTimeout(() => {
        navigate("/app/chat");
      }, 300);
    }
    setLoading(false);
  };

  const handleApplycoupon = async (e) => {
    e.stopPropagation();
    if (!coupon) return;
    setCouponLoading(true);
    try {
      const response = await applyCouponMutation.mutateAsync({
        userId: userActiveLocation?._id,
        code: coupon,
        agency_id: userActiveLocation?.agency_id,
      });
      if (response?.data) {
        setCouponDiscount(response?.data);
        toast.success("Coupon applied successfully");
      } else {
        toast.error("Failed to apply coupon");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to apply coupon");
    }
    setCouponLoading(false);
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="space-y-2">
        <input
          type="text"
          placeholder="Name of Cardholder"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full outline-none border border-border rounded-md px-4 py-3 text-base focus:border-ring transition-colors placeholder:text-muted-foreground bg-background text-foreground"
          required
        />
        <div className="w-full border border-border rounded-md px-4 py-3 focus-within:border-ring transition-colors bg-background">
          <CardNumberElement key={resolvedTheme} options={cardOptions} />
        </div>
        <input
          type="text"
          placeholder="Billing Postcode"
          value={postcode}
          onChange={(e) => setPostcode(e.target.value)}
          className="w-full border border-border rounded-md px-4 py-3 text-base focus:border-ring transition-colors placeholder:text-muted-foreground bg-background text-foreground"
        />
        <div className="grid grid-cols-2 gap-3">
          <div className="w-full border border-border rounded-md px-4 py-3 focus-within:border-ring transition-colors bg-background">
            <CardCvcElement
              key={resolvedTheme}
              options={{ ...cardOptions, placeholder: "CVC" }}
            />
          </div>
          <div className="w-full border border-border rounded-md px-4 py-3 focus-within:border-ring transition-colors bg-background">
            <CardExpiryElement
              key={resolvedTheme}
              options={{ ...cardOptions, placeholder: "MM/YY" }}
            />
          </div>
        </div>
      </div>

      <div className="pt-2">
        {!showCoupon ? (
          <button
            type="button"
            onClick={() => setShowCoupon(true)}
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            Have a voucher code?
          </button>
        ) : (
          <div className="flex gap-2 animate-in fade-in duration-300">
            <input
              type="text"
              placeholder="Add coupon code here"
              value={coupon}
              onChange={(e) => setCoupon(e.target.value)}
              className="flex-1 border border-border rounded-md px-3 py-2 text-sm focus:border-ring transition-colors bg-background text-foreground"
            />
            <button
              onClick={handleApplycoupon}
              disabled={couponLoading || !coupon}
              className="px-4 py-2 bg-foreground text-background rounded-md text-xs font-bold disabled:bg-muted disabled:text-muted-foreground transition-colors"
            >
              {couponLoading ? "..." : "Apply"}
            </button>
          </div>
        )}
      </div>

      {error && <p className="text-red-500 text-sm">{error}</p>}

      <button
        type="submit"
        disabled={
          loading ||
          !postcode ||
          !name ||
          !elements?.getElement(CardNumberElement)
        }
        className={`w-full font-bold py-4 rounded-md transition-all text-[15px] ${loading ||
          (postcode && name && elements?.getElement(CardNumberElement))
          ? "bg-foreground text-background shadow-sm hover:bg-foreground/90"
          : "bg-muted text-muted-foreground cursor-not-allowed"
          }`}
      >
        {loading ? "Processing..." : "Start my FREE Trial"}
      </button>
    </form>
  );
};
